import { ApiResponse } from "@/types/global";
import { AxiosError, AxiosResponse, HttpStatusCode } from "axios";

export function handleApiResponse(
  response: AxiosResponse,
  isPaginated: boolean = false,
): ApiResponse {
  return {
    isSuccess: true,
    statusCode: HttpStatusCode.Ok,
    data: isPaginated ? response.data.items : response.data,
    count: isPaginated ? response.data.count : undefined,
    pages: isPaginated ? response.data.pages : undefined,
    errorMessage: undefined,
  };
}

// Every route to /under-maintenance — a dead ping, a CORS block, a timeout, and any exception
// thrown during bootstrap — used to land on one screen with nothing logged, so a user's
// screenshot could not tell an unreachable API apart from a JavaScript error on their browser.
// These two produce a short code for that screen (and the console) so it can.
//
// They live here, beside handleApiError, rather than in appUtils because sessionStore calls the
// first one: appUtils imports sessionStore, so importing it back would close a module cycle.
//
// `NET-<status>` means the server-clock ping answered with that status; `NET-0` specifically is
// handleApiError below reporting no response at all — a network error, a blocked host, or the
// 10s timeout on getServerClock.
const PING_REASON_PREFIX = "NET-";

export function getPingReasonCode(statusCode: number | null): string {
  return `${PING_REASON_PREFIX}${statusCode ?? 0}`;
}

// The two codes are retired by different events, so they must be told apart. A healthy ping
// retires a NET code, but it must NOT retire a BOOT code: bootstrap runs *after* the ping has
// already succeeded, so a healthy ping is exactly the state a bootstrap failure is recorded in.
// Clearing on any successful ping wiped the BOOT code before the failure screen could show it.
export function isPingReasonCode(reason: string | null): boolean {
  return reason !== null && reason.startsWith(PING_REASON_PREFIX);
}

// `BOOT-<name>` means the ping succeeded but bootstrap threw. The error name is what carries the
// signal: `BOOT-Error` is one of our own API failures re-thrown by initializeAppConfig, whereas
// `BOOT-TypeError` is the app calling something the browser does not implement — the shape a
// browser too old for the bundle produces.
export function getBootFailureCode(error: unknown): string {
  return `BOOT-${error instanceof Error ? error.name : typeof error}`;
}

export function handleApiError(error: unknown): ApiResponse {
  let statusCode = HttpStatusCode.InternalServerError;
  const defaultMessage = "Unknown Error: An error has occurred while processing your request.";
  let errorMessage = defaultMessage;
  if (error instanceof AxiosError) {
    if (!error.response) {
      return {
        isSuccess: false,
        data: undefined,
        count: undefined,
        pages: undefined,
        statusCode: 0,
        errorMessage: "Server unavailable: Please try again after some time.",
      };
    }
    statusCode = error.response.status;
    if (statusCode === HttpStatusCode.BadRequest) {
      errorMessage = "Client error: Possible bad request.";
    } else if (statusCode === HttpStatusCode.Forbidden) {
      errorMessage = "Forbidden: Your session has expired. Please log in again.";
    } else if (statusCode === HttpStatusCode.TooManyRequests) {
      errorMessage = "Rate Limited: Too many requests, please try again later.";
    } else if (statusCode === HttpStatusCode.ServiceUnavailable) {
      errorMessage = "Service unavailable: The server is temporarily under maintenance.";
    } else if (error.message === "Network Error") {
      errorMessage = "No internet connection: Please check your network connection.";
    } else {
      // Every error the API returns carries `{"detail": "<string>"}` — except a 422, whose detail
      // is an ARRAY of `{loc, msg, type}` validation entries. An array is truthy, so assigning it
      // straight through put a non-string into `errorMessage` and every toast rendered it as
      // "[object Object]". Anything that is not a non-empty string falls back to the default copy.
      // `data` is optional-chained because a JSON body of literal `null` would otherwise throw
      // inside this handler and reject the service call instead of returning an ApiResponse.
      const detail = error.response.data?.detail;
      errorMessage = typeof detail === "string" && detail ? detail : defaultMessage;
    }
  }
  return {
    isSuccess: false,
    data: undefined,
    count: undefined,
    pages: undefined,
    statusCode: statusCode,
    errorMessage: errorMessage,
  };
}
